import React from 'react';
import { postRequest, batchRequest, getRequest } from "../api/ApiService";
import Auth from "../routes/Auth";
import jstz from "jstz";
import moment from "moment-timezone";
import { logError } from "../services/utils";
import EventEmitter from "../services/event-emitter";
import { reactLocalStorage } from "reactjs-localstorage";

const AUTHORIZATION_ERROR =
  "You do not have authorization to submit %ROLE% decisions.";

class SubmitDecisions {
  submitDecisionsRequest(type) {
    return postRequest(`${process.env.REACT_APP_API_URL}teams/submit_${type}/`);
  }

  getUnacceptedDecisionsRequest(temporaryUser = null) {
    let apiURL = `${process.env.REACT_APP_API_URL}teams/unaccepted_decisions/`;
    if (temporaryUser) {
      apiURL = `${apiURL}?team_id=${temporaryUser.team_id}`;
    }

    return apiURL;
  }

  getSubmissionDateRequest(
    type,
    round_number = null,
    is_round_finished = false,
    temporaryUser = null,
  ) {
    const game_id = temporaryUser ? temporaryUser.game_id : Auth.getGameId();
    const base_url = `${process.env.REACT_APP_API_URL}games/${game_id}/journal/`;
    const literal = is_round_finished ? (temporaryUser ? "&" : "?") : "&";
    const additionalQuery = is_round_finished
      ? temporaryUser
        ? `?team_id=${temporaryUser.team_id}`
        : ""
      : temporaryUser
        ? `&team_id=${temporaryUser.team_id}`
        : "";

    let result_url =
      is_round_finished === true
        ? `${process.env.REACT_APP_API_URL}teams/submit_${type}/${additionalQuery}`
        : `${base_url}?path=/api/teams/submit_${type}/${additionalQuery}${literal}only_last=true`;
    if (round_number !== null && round_number !== false) {
      result_url =
        is_round_finished === true
          ? `${result_url}${literal}round_number=${round_number}`
          : `${result_url}&round_number=${round_number}`;
    }

    return result_url;
  }

  updateSubmissionFromJournal(types, round_number, callback) {
    const keys = {
      pd: 'submit_development',
      pm: 'submit_marketing',
      op: 'submit_operations',
      fn: 'submit_finance'
    };

    let result = {};
    getRequest(`${process.env.REACT_APP_API_URL}games/${Auth.getGameId()}/journal/?round_number=${round_number}`)
      .then(res => {
        const timezone = jstz.determine();
        let history = [
          ...res.body,
        ];
        history.sort((a, b) => {
          const dateA = new Date(a.requested_at);
          const dateB = new Date(b.requested_at);

          return dateB - dateA;
        });

        types.forEach(type => {
          result[type] = {
            date: 'N/A',
            unaccepted: 0,
            success: false,
          };

          const obj = history.find(i => i.path === `/api/teams/${keys[type]}/`);
          if (obj && obj.response && typeof obj.response.success !== 'undefined') {
            result[type].success = obj.response.success;
            if (result[type].success === true) {
              result[type].date = moment(obj.requested_at).tz(timezone.name()).format('MM/DD hh:mmA');
            }
          }
        });

        if (callback) {
          callback(result);
        }
      })
      .catch(err => { });
  }

  getSubmissionRequest(
    types,
    round_number = null,
    is_round_finished = false,
    temporaryUser = null,
  ) {
    let requests = [
      // eslint-disable-next-line
      getUnacceptedDecisionsRequest(temporaryUser)
    ];

    types.forEach(type => {
      let param = "";
      if (type === "pd") {
        param = "development";
      } else if (type === "pm") {
        param = "marketing";
      } else if (type === "op") {
        param = "operations";
      } else if (type === "fn") {
        param = "finance";
      }

      if (param !== "") {
        requests.push(
          // eslint-disable-next-line
          getSubmissionDateRequest(
            param,
            round_number,
            is_round_finished,
            temporaryUser,
          )
        );
      }
    });

    return requests;
  }

  handleSubmission(types, res, is_round_finished = false) {
    let data = {};

    types.forEach(type => {
      data[type] = {
        unaccepted: 0,
        date: "N/A"
      };
    });

    res.forEach((r, i) => {
      if (i === 0) {
        Object.keys(r).forEach(field => {
          if (types.includes(field.toLowerCase())) {
            data[field.toLowerCase()].unaccepted = r[field];
          }
        });
      } else {
        const timezone = jstz.determine();

        if (is_round_finished === true) {
          const type = types[i - 1];

          data[type].date =
            r.length > 0
              ? moment()
                .tz(timezone.name())
                .format("MM/DD hh:mmA")
              : "N/A";
          data[type].unaccepted = 0;
        } else {
          let date = "";

          if (!r[0] || r[0].requested_at === null) {
            date = "N/A";
          } else {
            date = moment(r[0].requested_at)
              .tz(timezone.name())
              .format("MM/DD hh:mmA");
          }

          const type = types[i - 1];
          data[type].date = date;
        }
      }
    });

    return data;
  }

  getSubmissionData(
    types,
    temporaryUser = null,
  ) {
    let requests = [
      // eslint-disable-next-line
      getUnacceptedDecisionsRequest(temporaryUser)
    ];

    types.forEach(type => {
      let param = "";
      if (type === "pd") {
        param = "development";
      } else if (type === "pm") {
        param = "marketing";
      } else if (type === "op") {
        param = "operations";
      } else if (type === "fn") {
        param = "finance";
      }

      if (param !== "") {
        requests.push(
          // eslint-disable-next-line
          getSubmissionDateRequest(param, null, undefined, temporaryUser)
        );
      }
    });

    const joinRequest = batchRequest(requests);

    return joinRequest
      .then(res => {
        let data = {};

        types.forEach(type => {
          data[type] = {
            unaccepted: 0,
            date: "N/A"
          };
        });

        res.forEach((r, i) => {
          const rq = r.body;

          if (i === 0) {
            Object.keys(rq).forEach(field => {
              if (types.includes(field.toLowerCase())) {
                data[field.toLowerCase()].unaccepted = rq[field];
              }
            });
          } else {
            let date = "";

            if (!rq[0] || rq[0].requested_at === null) {
              date = "N/A";
            } else {
              const timezone = jstz.determine();
              date = moment(rq[0].requested_at)
                .tz(timezone.name())
                .format("MM/DD hh:mmA");
            }

            const type = types[i - 1];
            data[type].date = date;
          }
        });

        return new Promise((resolve, reject) => {
          resolve(data);

          return null;
        });
      })
      .catch(err => {
        logError(err, "SubmitDecisions", "getSubmissionData");
      });
  }

  incrementUnacceptedDecisions(type) {
    let value =
      Number(reactLocalStorage.get(`${type}-unaccepted-decisions`, "0")) + 1;
    if (value === 0) {
      value = 1;
    }

    reactLocalStorage.set(`${type}-unaccepted-decisions`, value);
  }

  decrementUnacceptedDecisions(type) {
    let value =
      Number(reactLocalStorage.get(`${type}-unaccepted-decisions`, "0")) - 1;
    if (value === 0) {
      value = -1;
    }

    reactLocalStorage.set(`${type}-unaccepted-decisions`, value);
  }

  checkJournal(type, eventemitter, unaccepted_types, roundNumber, callback) {
    getRequest(`${process.env.REACT_APP_API_URL}games/${Auth.getGameId()}/journal/?round_number=${roundNumber}`)
      .then(res => {
        if (type === 'development') {
          let history = res.body.filter(i => (
            i.round_number === roundNumber && (
              i.path.startsWith('/api/team/products/') ||
              i.path.startsWith('/api/teams/submit_development/')
            )
          ));

          history.sort((a, b) => {
            const dateA = new Date(a.requested_at);
            const dateB = new Date(b.requested_at);

            return dateB - dateA;
          });

          if (history.length > 0 && history[0].path === '/api/teams/submit_development/' && (history[0].status_code === 200 || history[0].status_code === 201)) {
            eventemitter.trigger("submit-decisions-event", [
              "success",
              (
                <div>
                  Your decisions were successfully submitted.
                  Your financials have been updated.
                </div>
              )
            ]);

            if (unaccepted_types.includes(type)) {
              reactLocalStorage.set(`${type}-unaccepted-decisions`, 0);
            }
          }
        } else if (type === 'marketing') {
          const history = res.body.filter(i => (
            i.round_number === roundNumber && (
              i.path.startsWith('/api/team/price_forecasts/') ||
              i.path.startsWith('/api/team/promos/') ||
              i.path.startsWith('/api/team/sales/')
            )
          ));

          if (history.length > 0) {
            eventemitter.trigger("submit-decisions-event", [
              "success",
              (
                <div>
                  Your decisions were successfully submitted.
                  Your financials have been updated.
                </div>
              )
            ]);

            if (unaccepted_types.includes(type)) {
              reactLocalStorage.set(`${type}-unaccepted-decisions`, 0);
            }
          } else {
            eventemitter.trigger("submit-decisions-event", [
              "info",
              (
                <div>
                  You haven't made a marketing decision this
                  round therefore there's no decision to post.
                </div>
              )
            ]);
          }
        } else if (type === 'operations') {
          let history = res.body.filter(i => (
            i.round_number === roundNumber && (
              i.path.startsWith('/api/team/factories/') ||
              i.path.startsWith('/api/team/machines/') ||
              i.path.startsWith('/api/team/product_manufactures/') ||
              i.path.startsWith('/api/teams/submit_operations/')
            )
          ));
          history = history.sort((a, b) => {
            const dateA = new Date(a.requested_at);
            const dateB = new Date(b.requested_at);

            return dateB - dateA;
          });

          if (history.length > 0 && history[0].path === '/api/teams/submit_operations/' && (history[0].status_code === 200 || history[0].status_code === 201)) {
            eventemitter.trigger("submit-decisions-event", [
              "success",
              (
                <div>
                  Your decisions were successfully submitted.
                  Your financials have been updated.
                </div>
              )
            ]);

            if (unaccepted_types.includes(type)) {
              reactLocalStorage.set(`${type}-unaccepted-decisions`, 0);
            }
          } else {
            eventemitter.trigger("submit-decisions-event", [
              "info",
              (
                <div>
                  You haven't made an operation decision this
                  round therefore there's no decision to post.
                </div>
              )
            ]);
          }
        } else if (type === 'finance') {
          const history = res.body.filter(i => (
            i.round_number === roundNumber && (
              i.path.startsWith('/api/team/equities/') ||
              i.path.startsWith('/api/team/debts/') ||
              i.path.startsWith('/api/team/debt_repayments/') ||
              i.path.startsWith('/api/team/dividends_payment/') ||
              i.path.startsWith('/api/team/stock_repurchased/')
            )
          ));

          if (history.length > 0) {
            eventemitter.trigger("submit-decisions-event", [
              "success",
              (
                <div>
                  Your decisions were successfully submitted.
                  Your financials have been updated.
                </div>
              )
            ]);

            if (unaccepted_types.includes(type)) {
              reactLocalStorage.set(`${type}-unaccepted-decisions`, 0);
            }
          } else {
            eventemitter.trigger("submit-decisions-event", [
              "info",
              (
                <div>
                  You haven't made a finance decision this
                  round therefore there's no decision to post.
                </div>
              )
            ]);
          }
        }

        if (callback) {
          callback();
        }
      })
      .catch(err => { });
  }

  handleSubmittedRequest(success, type, eventemitter, unaccepted_types, roundNumber, callback) {
    if (success === false) {
      eventemitter.trigger("submit-decisions-event", [
        "error",
        (
          <div>
            You do not have sufficient cash to submit this decision.<br />
            Please check with your finance manager to ensure there's sufficient
            capital and if so, confirm that finance decisions were submitted (i.e. posted).
          </div>
        )
      ]);

      if (callback) {
        callback();
      }

      return;
    }

    if (type === 'development') {
      getRequest(`${process.env.REACT_APP_API_URL}team/products/?round_number=${roundNumber}`)
        .then(res => {
          const months_available_to_produce = res.body.reduce((prev, curr) => {
            return prev + curr.months_available_to_produce;
          }, 0);

          if (months_available_to_produce > 0) {
            checkJournal(type, eventemitter, unaccepted_types, roundNumber, callback);
          } else {
            eventemitter.trigger("submit-decisions-event", [
              "warning",
              (
                <div>
                  Your financials have been updated, but there's a warning.<br />
                  <b style={{ color: '#ffb940' }}>Warning</b>: You do not have at least 1 product
                  that is set to be released this year. Will this impact your performance?
                </div>
              )
            ]);

            if (callback) {
              callback();
            }
          }
        })
        .catch(err => { });
    } else if (type === 'marketing') {
      batchRequest([
        `${process.env.REACT_APP_API_URL}team/price_forecasts/?round_number=${roundNumber}`,
        `${process.env.REACT_APP_API_URL}team/promos/?round_number=${roundNumber}`,
        `${process.env.REACT_APP_API_URL}team/sales/?round_number=${roundNumber}`,
      ])
        .then(res => {
          const hasPrice0 = res[0].body.filter(i => i.price === 0).length > 0;
          const spend = res[1].body.reduce((prev, curr) => {
            return prev + curr.spend;
          }, 0);
          const total = res[2].body.reduce((prev, curr) => {
            return prev + curr.total;
          }, 0);

          if (hasPrice0 === true || spend === 0 || total === 0) {
            eventemitter.trigger("submit-decisions-event", [
              "warning",
              (
                <div>
                  Your financials have been updated, but there's a warning.<br />
                  <b style={{ color: '#ffb940' }}>Warning</b>: You haven't made all of your
                  decisions. Please make sure you've made pricing, promo, and sales channel decisions.
                </div>
              )
            ]);

            if (callback) {
              callback();
            }
          } else {
            checkJournal(type, eventemitter, unaccepted_types, roundNumber, callback);
          }
        })
        .catch(err => { });
    } else if (type === 'operations') {
      getRequest(`${process.env.REACT_APP_API_URL}team/inventory_balances/?round_number=${roundNumber}`)
        .then(res => {
          const available_for_sale = res.body.reduce((prev, curr) => {
            return prev + curr.available_for_sale;
          }, 0);

          if (available_for_sale > 100000) {
            checkJournal(type, eventemitter, unaccepted_types, roundNumber, callback);
          } else {
            eventemitter.trigger("submit-decisions-event", [
              "warning",
              (
                <div>
                  Your financials have been updated, but there's a warning.<br />
                  <b style={{ color: '#ffb940' }}>Warning</b>: You have low inventory. Please
                  consider producing more units.
                </div>
              )
            ]);

            if (callback) {
              callback();
            }
          }
        })
        .catch(err => { });
    } else {
      checkJournal(type, eventemitter, unaccepted_types, roundNumber, callback);
    }
  }

  submitDecisions(type, roundNumber, callback) {
    const eventemitter = EventEmitter.getInstance();
    const role = Auth.getRole();

    if (type === "development" && role !== "CEO" && role !== "PD") {
      eventemitter.trigger("app-show-error", [
        AUTHORIZATION_ERROR.replace("%ROLE%", "product development")
      ]);
      callback();
      return;
    }

    if (type === "marketing" && role !== "CEO" && role !== "PM") {
      eventemitter.trigger("app-show-error", [
        AUTHORIZATION_ERROR.replace("%ROLE%", "product marketing")
      ]);
      callback();
      return;
    }

    if (type === "operations" && role !== "CEO" && role !== "OP") {
      eventemitter.trigger("app-show-error", [
        AUTHORIZATION_ERROR.replace("%ROLE%", "operations")
      ]);
      callback();
      return;
    }

    if (type === "finance" && role !== "CEO" && role !== "FI") {
      eventemitter.trigger("app-show-error", [
        AUTHORIZATION_ERROR.replace("%ROLE%", "finance")
      ]);
      callback();
      return;
    }

    const unaccepted_types = [
      "development",
      "marketing",
      "finance",
      "operations"
    ];

    // eslint-disable-next-line
    submitDecisionsRequest(type)
      .then(res => {
        let { success } = res.body;

        handleSubmittedRequest(success, type, eventemitter, unaccepted_types, roundNumber, callback);
      })
      .catch(err => {
        if (err.response.status === 400) {
          handleSubmittedRequest(false, type, eventemitter, unaccepted_types, roundNumber, callback);
        } else {
          logError(err, "SubmitDecisions", "submitDecisions");
        }
      });
  }
}

const submitDecisionsRequest = SubmitDecisions.prototype.submitDecisionsRequest;
const getUnacceptedDecisionsRequest =
  SubmitDecisions.prototype.getUnacceptedDecisionsRequest;
const getSubmissionDateRequest =
  SubmitDecisions.prototype.getSubmissionDateRequest;
const handleSubmittedRequest = SubmitDecisions.prototype.handleSubmittedRequest;

export const submitDecisions = SubmitDecisions.prototype.submitDecisions;
export const getSubmissionData = SubmitDecisions.prototype.getSubmissionData;
export const getSubmissionRequest =
  SubmitDecisions.prototype.getSubmissionRequest;
export const handleSubmission = SubmitDecisions.prototype.handleSubmission;
export const incrementUnacceptedDecisions =
  SubmitDecisions.prototype.incrementUnacceptedDecisions;
export const decrementUnacceptedDecisions =
  SubmitDecisions.prototype.decrementUnacceptedDecisions;
export const updateSubmissionFromJournal =
  SubmitDecisions.prototype.updateSubmissionFromJournal;
export const checkJournal = SubmitDecisions.prototype.checkJournal;



// WEBPACK FOOTER //
// ./src/api/SubmitDecisions.js